// container-report.component.ts
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { skip } from 'rxjs/operators';
import { PagebaseComponent } from '../pagebase/pagebase.component';
import { PageHelperService } from '../helpers/page-helper.service';
import { ApplicationStatusService } from '../services/application-status.service';
import { CustomerSettings } from '../services/customer-settings-service';
import { DateTimeHelper } from '../helpers/datetime';
import {
  ReportsService,
  ContainerReportDataDto,
  ContainerReportRequestDto,
  FileResponse
} from 'src/shared/service-proxies/service-proxies';

type ThumbImage = {
  label: string;
  url: string;
};

type MovementSortColumn = 'from' | 'departed' | 'arrived' | 'to';
type DamageSortColumn = 'actionDate' | 'fault' | 'description' | 'comment' | 'by' | 'image1Url' | 'image2Url';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-container-report',
  templateUrl: './container-report.component.html',
  styleUrls: ['./container-report.component.scss'],
  standalone: false
})
export class ContainerReportComponent extends PagebaseComponent implements OnInit {
  loading = false;
  errorMessage: string | null = null;

  report: ContainerReportDataDto | null = null;
  customerId: string;
  damageDetailsUrl: string | null = null;

  // images / popup
  thumbnails: ThumbImage[] = [];
  isPopupOpen = false;
  selectedImage: string | null = null;

  movementSort: { column: MovementSortColumn | null; direction: SortDirection } = {
    column: null,
    direction: 'asc'
  };
  damageSort: { column: DamageSortColumn | null; direction: SortDirection } = {
    column: null,
    direction: 'asc'
  };
  movementRange: { min: number | null; max: number | null; from: number | null; to: number | null } = {
    min: null,
    max: null,
    from: null,
    to: null
  };
  readonly movementRangeStep = 24 * 60 * 60 * 1000;

  constructor(
    private route: ActivatedRoute,
    private containerService: ReportsService,
    pageHelperService: PageHelperService,
    applicationStatusService: ApplicationStatusService,
    customerSettings: CustomerSettings
  ) {
    super(pageHelperService, applicationStatusService, customerSettings, 'Container Report');
  }

  ngOnInit(): void {
    // Re-fetch if query params change (e.g. assetId navigation)
    this.route.queryParamMap
      .pipe(skip(1))
      .subscribe(() => this.getData());
  }

  getData(): void {
    const params = this.route.snapshot.paramMap;
    const assetId = params.get('assetId');
    if (!assetId) return;
    this.customerId = this.pageHelperService.selectedCompanyId;
    if (!this.customerId || this.customerId == "00000000-0000-0000-0000-000000000000") return;

    this.loading = true;
    this.setIsLoading(true);
    this.errorMessage = null;
    this.report = null;
    this.damageDetailsUrl = null;
    this.thumbnails = [];
    this.movementRange = { min: null, max: null, from: null, to: null };
    this.containerService
      .getContainerDetails(this.customerId, assetId)
      .subscribe({
        next: data => {
          this.report = data;
          this.thumbnails = this.buildThumbs(data);
          this.initMovementRange(data?.movements || []);

          const id = data?.assetId || assetId;
          this.damageDetailsUrl = this.buildDamageDetailsUrl(id);
          //this.pageHelperService.setPageTitle(id);

          this.loading = false;
          this.setIsLoading(false);
        },
        error: () => {
          this.errorMessage = '';
          this.loading = false;
          this.setIsLoading(false);
        }
      });
  }

  openPopup(url: string | null | undefined): void {
    const normalized = this.normalizeImageUrl(url);
    if (!normalized) return;
    this.selectedImage = normalized;
    this.isPopupOpen = true;
  }

  closePopup(): void {
    this.isPopupOpen = false;
  }

  // ---- formatting ----
  formatDateAndTime(value: any): string {
    return DateTimeHelper.formatDateAndTime(value);
  }

  formatDate(value: any): string {
    if (!value && value !== 0) return '-';
    return DateTimeHelper.formatDate(value);
  }

  trackByUrl(index: number, item: ThumbImage): string {
    return item.url;
  }

  reset(): void {}

  exportContainerDetails(): void {
    const request = this.buildExportRequest();
    if (!request) return;

    this.loading = true;
    this.setIsLoading(this.loading);
    this.containerService.exportContainerDetails(request).subscribe({
      next: data => this.handleExportResponse(data),
      error: () => this.handleExportResponse(null)
    });
  }

  exportContainerMovements(): void {
    const request = this.buildExportRequest();
    if (!request) return;

    this.loading = true;
    this.setIsLoading(this.loading);
    this.containerService.exportContainerMovements(request).subscribe({
      next: data => this.handleExportResponse(data),
      error: () => this.handleExportResponse(null)
    });
  }

  exportContainerDamage(): void {
    const request = this.buildExportRequest();
    if (!request) return;

    this.loading = true;
    this.setIsLoading(this.loading);
    this.containerService.exportContainerDamage(request).subscribe({
      next: data => this.handleExportResponse(data),
      error: () => this.handleExportResponse(null)
    });
  }

  // ---- sorting ----
  toggleMovementSort(column: MovementSortColumn): void {
    if (this.movementSort.column === column) {
      this.movementSort.direction = this.movementSort.direction === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.movementSort.column = column;
    this.movementSort.direction = 'asc';
  }

  toggleDamageSort(column: DamageSortColumn): void {
    if (this.damageSort.column === column) {
      this.damageSort.direction = this.damageSort.direction === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.damageSort.column = column;
    this.damageSort.direction = 'asc';
  }

  movementSortIndicator(column: MovementSortColumn): string {
    if (this.movementSort.column !== column) return '';
    return this.movementSort.direction === 'asc' ? '^' : 'v';
  }

  damageSortIndicator(column: DamageSortColumn): string {
    if (this.damageSort.column !== column) return '';
    return this.damageSort.direction === 'asc' ? '^' : 'v';
  }

  sortedMovements(): any[] {
    const data = this.filteredMovements();
    if (!this.movementSort.column) return data;
    const column = this.movementSort.column;
    const direction = this.movementSort.direction;

    return [...data].sort((a, b) => {
      const aVal = this.getMovementSortValue(a, column);
      const bVal = this.getMovementSortValue(b, column);
      return this.compareValues(aVal, bVal, direction);
    });
  }

  filteredMovements(): any[] {
    const data = this.report?.movements || [];
    const { from, to } = this.movementRange;
    if (from === null || to === null) return data;

    return data.filter(row => {
      const ts = this.getMovementFilterTimestamp(row);
      if (ts === null) return false;
      return ts >= from && ts <= to;
    });
  }

  movementCount(): number | string {
    if (!this.report?.movements) return '-';
    return this.filteredMovements().length;
  }

  onMovementFromInput(event: Event): void {
    const value = this.normalizeMovementRangeValue(Number((event.target as HTMLInputElement).value));
    if (value === null) return;

    if (this.movementRange.to !== null && value > this.movementRange.to) {
      this.movementRange.to = value;
    }
    this.movementRange.from = value;
  }

  onMovementToInput(event: Event): void {
    const value = this.normalizeMovementRangeValue(Number((event.target as HTMLInputElement).value));
    if (value === null) return;

    if (this.movementRange.from !== null && value < this.movementRange.from) {
      this.movementRange.from = value;
    }
    this.movementRange.to = value;
  }

  onMovementFromDateInput(event: Event): void {
    const value = this.parseMovementDateValue((event.target as HTMLInputElement).value);
    if (value === null) return;

    if (this.movementRange.to !== null && value > this.movementRange.to) {
      this.movementRange.to = value;
    }
    this.movementRange.from = value;
  }

  onMovementToDateInput(event: Event): void {
    const value = this.parseMovementDateValue((event.target as HTMLInputElement).value);
    if (value === null) return;

    if (this.movementRange.from !== null && value < this.movementRange.from) {
      this.movementRange.from = value;
    }
    this.movementRange.to = value;
  }

  movementFromDateValue(): string {
    return this.toInputDateValue(this.movementRange.from);
  }

  movementToDateValue(): string {
    return this.toInputDateValue(this.movementRange.to);
  }

  movementMinDateValue(): string {
    return this.toInputDateValue(this.movementRange.min);
  }

  movementMaxDateValue(): string {
    return this.toInputDateValue(this.movementRange.max);
  }

  movementFromPercent(): string {
    return this.getMovementPercent(this.movementRange.from);
  }

  movementToPercent(): string {
    return this.getMovementPercent(this.movementRange.to);
  }

  sortedDamage(): any[] {
    const data = this.report?.damage || [];
    if (!this.damageSort.column) return data;
    const column = this.damageSort.column;
    const direction = this.damageSort.direction;

    return [...data].sort((a, b) => {
      const aVal = this.getDamageSortValue(a, column);
      const bVal = this.getDamageSortValue(b, column);
      return this.compareValues(aVal, bVal, direction);
    });
  }

  // ---- helpers ----
  private buildThumbs(data: ContainerReportDataDto | null): ThumbImage[] {
    const out: ThumbImage[] = [];
    const image1Url = this.normalizeImageUrl(data?.image1Url);
    const image2Url = this.normalizeImageUrl(data?.image2Url);

    if (image1Url) out.push({ label: 'Image 1', url: image1Url });
    if (image2Url) out.push({ label: 'Image 2', url: image2Url });

    return out;
  }

  private buildDamageDetailsUrl(assetId: string | null | undefined): string | null {
    if (!assetId) return null;
    return `${window.location.origin}/damage/${assetId}`;
  }

  private normalizeImageUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    const trimmed = url.trim();
    if (!trimmed) return null;

    // Some endpoints return HTML-encoded query strings (e.g. &amp;), which breaks SAS signatures.
    const deEnt = trimmed.replace(/&amp;/g, '&');

    // Ensure spaces don't invalidate the URL.
    return deEnt.includes(' ') ? encodeURI(deEnt) : deEnt;
  }

  private initMovementRange(movements: any[]): void {
    const timestamps = (movements || [])
      .map(row => this.getMovementFilterTimestamp(row))
      .filter((value): value is number => value !== null);

    if (!timestamps.length) {
      this.movementRange = { min: null, max: null, from: null, to: null };
      return;
    }

    const min = Math.min(...timestamps);
    const max = Math.max(...timestamps);
    this.movementRange = { min, max, from: min, to: max };
  }

  private getMovementPercent(value: number | null): string {
    const { min, max } = this.movementRange;
    if (value === null || min === null || max === null || min === max) return '0%';
    const pct = ((value - min) / (max - min)) * 100;
    return `${Math.min(100, Math.max(0, pct))}%`;
  }

  private parseMovementDateValue(value: string): number | null {
    if (!value) return null;
    const parsed = moment(value, 'YYYY-MM-DD', true);
    if (!parsed.isValid()) return null;
    return this.normalizeMovementRangeValue(parsed.startOf('day').valueOf());
  }

  private toInputDateValue(value: number | null): string {
    if (value === null) return '';
    return moment(value).format('YYYY-MM-DD');
  }

  private normalizeMovementRangeValue(value: number): number | null {
    const { min, max } = this.movementRange;
    if (!Number.isFinite(value) || min === null || max === null) return null;
    return Math.min(max, Math.max(min, moment(value).startOf('day').valueOf()));
  }

  private getMovementSortValue(row: any, column: MovementSortColumn): any {
    if (!row) return null;
    switch (column) {
      case 'departed':
      case 'arrived':
        return row[column] ? moment(row[column]).valueOf() : null;
      default:
        return row[column] ?? null;
    }
  }

  private getMovementFilterTimestamp(row: any): number | null {
    if (!row) return null;
    const value = row.departed ?? row.arrived;
    if (!value) return null;
    return moment(value).startOf('day').valueOf();
  }

  private getDamageSortValue(row: any, column: DamageSortColumn): any {
    if (!row) return null;
    switch (column) {
      case 'actionDate':
        return row.actionDate ? moment(row.actionDate).valueOf() : null;
      case 'image1Url':
        return row.image1Url ? 1 : 0;
      case 'image2Url':
        return row.image2Url ? 1 : 0;
      default:
        return row[column] ?? null;
    }
  }

  private compareValues(a: any, b: any, direction: SortDirection): number {
    const sortDir = direction === 'asc' ? 1 : -1;
    const aNil = a === null || a === undefined || a === '';
    const bNil = b === null || b === undefined || b === '';

    if (aNil && bNil) return 0;
    if (aNil) return 1 * sortDir;
    if (bNil) return -1 * sortDir;

    if (typeof a === 'number' && typeof b === 'number') {
      return a === b ? 0 : a > b ? 1 * sortDir : -1 * sortDir;
    }

    const aStr = String(a).toLowerCase();
    const bStr = String(b).toLowerCase();
    return aStr.localeCompare(bStr) * sortDir;
  }

  private buildExportRequest(): ContainerReportRequestDto | null {
    this.customerId = this.pageHelperService.selectedCompanyId;
    if (!this.customerId || this.customerId == "00000000-0000-0000-0000-000000000000") {
      return null;
    }

    const assetId = this.report?.assetId || this.route.snapshot.paramMap.get('assetId');
    if (!assetId) {
      return null;
    }

    return { customerId: this.customerId, assetId };
  }

  private handleExportResponse(data: FileResponse | null): void {
    this.loading = false;
    this.setIsLoading(this.loading);

    if (!data) {
      return;
    }

    const filename = data.fileName;
    const blob: Blob = data.data;
    const a = document.createElement('a');
    a.download = filename;
    a.href = window.URL.createObjectURL(blob);
    a.click();
  }
}
